import React, { useState, useEffect, useCallback } from "react";
import { Alert, Spinner, Badge, Modal, Button } from "react-bootstrap";
import styles from "./VoucherManagement.module.css";
import { useAuth } from "../../context/AuthContext";
import { getAccessToken } from "../../../utils/authStorage";

const API_URL = (
  import.meta.env.VITE_API_URL || "https://pixelmoonstore.in/api/v1"
).replace(/\/$/, "");

const VoucherManagement = () => {
  const [activeTab, setActiveTab] = useState("list");

  // ─── LIST state ───
  const [vouchers, setVouchers] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [listError, setListError] = useState(null);
  const [filter, setFilter] = useState({
    type: "",
    status: "active",
    page: 1,
    limit: 25,
  });
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  // ─── CREATE CUSTOM state ───
  const [customForm, setCustomForm] = useState({
    name: "",
    code: "",
    type: "manual",
    denomination: "",
    price: "",
    description: "",
    expiresAt: "",
    maxUses: 1,
  });
  const [creatingCustom, setCreatingCustom] = useState(false);
  const [customError, setCustomError] = useState(null);
  const [customSuccess, setCustomSuccess] = useState(null);

  // ─── CREATE (JSON rows) state ───
  const [rows, setRows] = useState([
    {
      code: "",
      type: "smileone",
      denomination: "",
      price: "",
      name: "",
      description: "",
    },
  ]);
  const [creatingJSON, setCreatingJSON] = useState(false);
  const [createJSONResult, setCreateJSONResult] = useState(null);
  const [createJSONError, setCreateJSONError] = useState(null);

  // ─── CREATE (CSV) state ───
  const [csvFile, setCsvFile] = useState(null);
  const [csvProgress, setCsvProgress] = useState(0);
  const [creatingCSV, setCreatingCSV] = useState(false);
  const [createCSVResult, setCreateCSVResult] = useState(null);
  const [createCSVError, setCreateCSVError] = useState(null);

  // ─── Edit Modal state ───
  const [editModal, setEditModal] = useState({
    show: false,
    id: null,
    data: {},
  });

  // ─── Archive Expired Modal ───
  const [archiveModal, setArchiveModal] = useState({ show: false });
  const token = useAuth().token;
  const getAuthHeaders = useCallback(
    () => ({
      Authorization: `Bearer ${token}`,
    }),
    [],
  );

  // ─── Fetch vouchers on filter change ───
  useEffect(() => {
    if (activeTab === "list") {
      fetchVouchers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter.page, filter.type, filter.status, activeTab]);

  const fetchVouchers = async () => {
    setLoadingList(true);
    setListError(null);
    try {
      const params = new URLSearchParams({
        type: filter.type,
        status: filter.status,
        page: filter.page,
        limit: filter.limit,
      });

      const res = await fetch(`${API_URL}/vouchers?${params}`, {
        headers: getAuthHeaders(),
      });

      if (!res.ok) {
        throw new Error(`HTTP ${res.status}: Failed to fetch vouchers`);
      }

      const data = await res.json();
      if (!data.success)
        throw new Error(data.message || "Failed to fetch vouchers");

      setVouchers(data.vouchers || []);
      setTotalCount(data.totalCount ?? 0);
      setTotalPages(data.totalPages ?? 1);
    } catch (err) {
      setListError(err.message);
      console.error("Fetch vouchers error:", err);
    } finally {
      setLoadingList(false);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilter((prev) => ({ ...prev, [name]: value, page: 1 }));
  };

  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > totalPages) return;
    setFilter((prev) => ({ ...prev, page: newPage }));
  };

  // ─── Custom Voucher Handlers ───
  const handleCustomFormChange = (e) => {
    const { name, value } = e.target;
    setCustomForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleCreateCustomSubmit = async (e) => {
    e.preventDefault();
    setCustomError(null);
    setCustomSuccess(null);

    // Validate
    if (
      !customForm.name ||
      !customForm.type ||
      !customForm.denomination ||
      !customForm.price
    ) {
      setCustomError("Please fill in all required fields");
      return;
    }

    if (Number(customForm.denomination) <= 0 || Number(customForm.price) <= 0) {
      setCustomError("Denomination and price must be positive numbers");
      return;
    }

    setCreatingCustom(true);

    try {
      const payload = {
        name: customForm.name.trim(),
        type: customForm.type,
        denomination: Number(customForm.denomination),
        price: Number(customForm.price),
        description: customForm.description.trim(),
        ...(customForm.expiresAt && {
          expiresAt: new Date(customForm.expiresAt).toISOString(),
        }),
        ...(customForm.maxUses && { maxUses: Number(customForm.maxUses) }),
      };

      const res = await fetch(`${API_URL}/vouchers/create-custom`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeaders(),
        },
        body: JSON.stringify(payload),
      });

      const result = await res.json();
      if (!result.success) throw new Error(result.message || "Creation failed");

      setCustomSuccess(`✅ Voucher created! Code: ${result.voucher.code}`);
      setCustomForm({
        name: "",
        code: "",
        type: "manual",
        denomination: "",
        price: "",
        description: "",
        expiresAt: "",
        maxUses: 1,
      });

      // Refresh list if viewing list tab
      if (activeTab === "list") fetchVouchers();
    } catch (err) {
      setCustomError(err.message);
      console.error("Create custom voucher error:", err);
    } finally {
      setCreatingCustom(false);
    }
  };

  // ─── JSON Rows Handlers ───
  const addRow = () => {
    setRows((prev) => [
      ...prev,
      {
        code: "",
        type: "smileone",
        denomination: "",
        price: "",
        name: "",
        description: "",
      },
    ]);
  };

  const removeRow = (index) => {
    if (rows.length === 1) {
      setCreateJSONError("At least one row is required");
      return;
    }
    setRows((prev) => prev.filter((_, i) => i !== index));
  };

  const handleRowChange = (index, field, value) => {
    setRows((prev) => {
      const copy = [...prev];
      copy[index][field] = value;
      return copy;
    });
  };

  const handleCreateJSONSubmit = async () => {
    setCreateJSONError(null);
    setCreateJSONResult(null);

    // Validate rows
    const invalids = rows.filter((r) => {
      const hasEmpty = !r.code.trim() || !r.name.trim() || !r.type;
      const hasInvalidNumbers =
        Number(r.denomination) <= 0 || Number(r.price) <= 0;
      return hasEmpty || hasInvalidNumbers;
    });

    if (invalids.length > 0) {
      setCreateJSONError(
        `${invalids.length} row(s) are invalid. Please ensure all fields are filled with positive numbers.`,
      );
      return;
    }

    setCreatingJSON(true);

    try {
      const payload = {
        vouchers: rows.map((r) => ({
          code: r.code.trim(),
          name: r.name.trim(),
          type: r.type,
          denomination: Number(r.denomination),
          price: Number(r.price),
          description: r.description.trim(),
        })),
      };

      const res = await fetch(`${API_URL}/vouchers`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeaders(),
        },
        body: JSON.stringify(payload),
      });

      const result = await res.json();
      if (!result.success) throw new Error(result.message || "Creation failed");

      setCreateJSONResult(result);
      setRows([
        {
          code: "",
          type: "smileone",
          denomination: "",
          price: "",
          name: "",
          description: "",
        },
      ]);

      if (activeTab === "list") fetchVouchers();
    } catch (err) {
      setCreateJSONError(err.message);
      console.error("Create JSON vouchers error:", err);
    } finally {
      setCreatingJSON(false);
    }
  };

  // ─── CSV Upload Handlers ───
  const onCsvFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setCreateCSVError("File size must be less than 5MB");
        setCsvFile(null);
        return;
      }
      if (!file.name.endsWith(".csv")) {
        setCreateCSVError("Please select a valid CSV file");
        setCsvFile(null);
        return;
      }
      setCsvFile(file);
      setCreateCSVError(null);
    }
  };

  const handleCreateCSVSubmit = async () => {
    if (!csvFile) {
      setCreateCSVError("Please select a CSV file");
      return;
    }

    setCreatingCSV(true);
    setCreateCSVResult(null);
    setCreateCSVError(null);
    setCsvProgress(0);

    const formData = new FormData();
    formData.append("csvFile", csvFile);

    try {
      const xhr = new XMLHttpRequest();

      xhr.upload.addEventListener("progress", (e) => {
        if (e.lengthComputable) {
          const percentComplete = (e.loaded / e.total) * 100;
          setCsvProgress(Math.round(percentComplete));
        }
      });

      xhr.addEventListener("load", () => {
        if (xhr.status === 201 || xhr.status === 200) {
          try {
            const result = JSON.parse(xhr.responseText);
            if (result.success) {
              setCreateCSVResult(result);
              setCsvFile(null);
              setCsvProgress(0);
              document.getElementById("voucher-csv-input").value = "";
              if (activeTab === "list") fetchVouchers();
            } else {
              throw new Error(result.message || "Upload failed");
            }
          } catch (err) {
            setCreateCSVError("Invalid response from server");
          }
        } else {
          try {
            const error = JSON.parse(xhr.responseText);
            setCreateCSVError(error.message || "Upload failed");
          } catch {
            setCreateCSVError(`Upload failed with status ${xhr.status}`);
          }
        }
        setCreatingCSV(false);
      });

      xhr.addEventListener("error", () => {
        setCreateCSVError("Network error during upload");
        setCreatingCSV(false);
      });

      xhr.setRequestHeader("Authorization", `Bearer ${getAccessToken()}`);
      xhr.open("POST", `${API_URL}/vouchers`);
      xhr.send(formData);
    } catch (err) {
      setCreateCSVError(err.message);
      setCreatingCSV(false);
    }
  };

  // ─── Update / Delete Handlers ───
  const openEditModal = (voucher) => {
    setEditModal({
      show: true,
      id: voucher._id,
      data: {
        price: voucher.price,
        status: voucher.status,
        expiresAt: voucher.expiresAt
          ? new Date(voucher.expiresAt).toISOString().split("T")[0]
          : "",
        maxUses: voucher.maxUses,
      },
    });
  };

  const handleUpdateVoucher = async () => {
    if (!editModal.id) return;

    try {
      const payload = {
        ...(editModal.data.price && { price: Number(editModal.data.price) }),
        ...(editModal.data.status && { status: editModal.data.status }),
        ...(editModal.data.expiresAt && {
          expiresAt: editModal.data.expiresAt,
        }),
        ...(editModal.data.maxUses && {
          maxUses: Number(editModal.data.maxUses),
        }),
      };

      const res = await fetch(`${API_URL}/vouchers/${editModal.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeaders(),
        },
        body: JSON.stringify(payload),
      });

      const result = await res.json();
      if (!result.success) throw new Error(result.message || "Update failed");

      setEditModal({ show: false, id: null, data: {} });
      fetchVouchers();
    } catch (err) {
      alert(`Error: ${err.message}`);
    }
  };

  const handleDeleteVoucher = async (id) => {
    if (
      !window.confirm(
        "⚠️ Are you sure you want to delete this voucher? This action cannot be undone.",
      )
    ) {
      return;
    }

    try {
      const res = await fetch(`${API_URL}/vouchers/${id}`, {
        method: "DELETE",
        headers: getAuthHeaders(),
      });

      const result = await res.json();
      if (!result.success) throw new Error(result.message || "Delete failed");

      alert("✅ Voucher deleted successfully");
      fetchVouchers();
    } catch (err) {
      alert(`Error: ${err.message}`);
    }
  };

  const handleArchiveExpired = async () => {
    try {
      const res = await fetch(`${API_URL}/vouchers/archive/expired`, {
        method: "POST",
        headers: getAuthHeaders(),
      });

      const result = await res.json();
      if (!result.success) throw new Error(result.message || "Archive failed");

      alert(`✅ ${result.archivedCount} expired vouchers archived`);
      setArchiveModal({ show: false });
      fetchVouchers();
    } catch (err) {
      alert(`Error: ${err.message}`);
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      active: "success",
      redeemed: "info",
      expired: "warning",
      archived: "secondary",
    };
    return variants[status] || "secondary";
  };

  const getTypeBadge = (type) => {
    const variants = {
      smileone: "info",
      moo: "warning",
      custom: "success",
    };
    return variants[type] || "secondary";
  };

  return (
    <div className={`container-fluid mt-4 ${styles.voucherManagement}`}>
      {/* ─── TAB NAVIGATION ─── */}
      <ul className="nav nav-tabs mb-4" role="tablist">
        <li className="nav-item" role="presentation">
          <button
            className={`nav-link ${activeTab === "list" ? "active" : ""}`}
            onClick={() => setActiveTab("list")}
            role="tab"
            aria-selected={activeTab === "list"}
            aria-controls="list-tab"
          >
            📋 List Vouchers
          </button>
        </li>
        <li className="nav-item" role="presentation">
          <button
            className={`nav-link ${activeTab === "create-custom" ? "active" : ""}`}
            onClick={() => setActiveTab("create-custom")}
            role="tab"
            aria-selected={activeTab === "create-custom"}
            aria-controls="create-custom-tab"
          >
            ➕ Create Single Voucher
          </button>
        </li>
        <li className="nav-item" role="presentation">
          <button
            className={`nav-link ${activeTab === "create-json" ? "active" : ""}`}
            onClick={() => setActiveTab("create-json")}
            role="tab"
            aria-selected={activeTab === "create-json"}
            aria-controls="create-json-tab"
          >
            📝 Bulk Create (JSON)
          </button>
        </li>
        <li className="nav-item" role="presentation">
          <button
            className={`nav-link ${activeTab === "create-csv" ? "active" : ""}`}
            onClick={() => setActiveTab("create-csv")}
            role="tab"
            aria-selected={activeTab === "create-csv"}
            aria-controls="create-csv-tab"
          >
            📤 Bulk Upload (CSV)
          </button>
        </li>
      </ul>

      {/* ─── LIST TAB ─── */}
      {activeTab === "list" && (
        <>
          <div className="row mb-4 g-3">
            <div className="col-md-3">
              <label className="form-label fw-bold">🎯 Type</label>
              <select
                className="form-select"
                name="type"
                value={filter.type}
                onChange={handleFilterChange}
                aria-label="Filter by voucher type"
              >
                <option value="">All Types</option>
                <option value="smileone">😊 Smile.one</option>
                <option value="moo">🐄 MOO</option>
                <option value="custom">⭐ Custom</option>
              </select>
            </div>

            <div className="col-md-3">
              <label className="form-label fw-bold">📊 Status</label>
              <select
                className="form-select"
                name="status"
                value={filter.status}
                onChange={handleFilterChange}
                aria-label="Filter by status"
              >
                <option value="active">✅ Active</option>
                <option value="redeemed">🎁 Redeemed</option>
                <option value="expired">⏰ Expired</option>
                <option value="archived">📦 Archived</option>
              </select>
            </div>

            <div className="col-md-3 d-flex align-items-end gap-2">
              <button
                className="btn btn-primary w-100"
                onClick={fetchVouchers}
                disabled={loadingList}
                aria-label="Refresh vouchers list"
              >
                {loadingList ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    Loading…
                  </>
                ) : (
                  "🔄 Refresh"
                )}
              </button>
            </div>

            <div className="col-md-3">
              <button
                className="btn btn-warning w-100"
                onClick={() => setArchiveModal({ show: true })}
                aria-label="Archive expired vouchers"
              >
                📦 Archive Expired
              </button>
            </div>
          </div>

          {listError && (
            <Alert
              variant="danger"
              dismissible
              onClose={() => setListError(null)}
            >
              <strong>Error:</strong> {listError}
            </Alert>
          )}

          {loadingList && (
            <div className="text-center py-5">
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Loading vouchers…</span>
              </Spinner>
            </div>
          )}

          {!loadingList && vouchers.length === 0 && (
            <Alert variant="info">
              No vouchers found. Create one to get started!
            </Alert>
          )}

          {!loadingList && vouchers.length > 0 && (
            <div className={styles.tableResponsive}>
              <table className="table table-hover table-striped">
                <thead className="table-dark sticky-top">
                  <tr>
                    <th>Code</th>
                    <th>Name</th>
                    <th>Type</th>
                    <th>Denomination</th>
                    <th>Price</th>
                    <th>Status</th>
                    <th>Uses</th>
                    <th>Expires</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {vouchers.map((v) => (
                    <tr key={v._id} className={styles.tableRow}>
                      <td className="font-monospace">{v.code}</td>
                      <td className="fw-bold">{v.name}</td>
                      <td>
                        <Badge bg={getTypeBadge(v.type)}>
                          {v.type.charAt(0).toUpperCase() + v.type.slice(1)}
                        </Badge>
                      </td>
                      <td className="text-success fw-bold">
                        ₹{v.denomination}
                      </td>
                      <td className="text-primary fw-bold">
                        ₹{v.price.toFixed(2)}
                      </td>
                      <td>
                        <Badge bg={getStatusBadge(v.status)}>
                          {v.status.charAt(0).toUpperCase() + v.status.slice(1)}
                        </Badge>
                      </td>
                      <td>
                        <small className="text-muted">
                          {v.usageCount}/{v.maxUses}
                        </small>
                      </td>
                      <td>
                        <small>
                          {new Date(v.expiresAt).toLocaleDateString()}
                        </small>
                      </td>
                      <td>
                        <button
                          className="btn btn-sm btn-outline-primary me-2"
                          onClick={() => openEditModal(v)}
                          title="Edit voucher"
                        >
                          ✏️
                        </button>
                        {v.status === "active" && (
                          <button
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => handleDeleteVoucher(v._id)}
                            title="Delete voucher"
                          >
                            🗑️
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {!loadingList && totalPages > 1 && (
            <div className="d-flex justify-content-between align-items-center mt-4">
              <button
                className="btn btn-outline-secondary"
                onClick={() => handlePageChange(filter.page - 1)}
                disabled={filter.page === 1}
              >
                ← Previous
              </button>
              <span className="text-muted">
                Page <strong>{filter.page}</strong> of{" "}
                <strong>{totalPages}</strong> ({totalCount} total)
              </span>
              <button
                className="btn btn-outline-secondary"
                onClick={() => handlePageChange(filter.page + 1)}
                disabled={filter.page === totalPages}
              >
                Next →
              </button>
            </div>
          )}
        </>
      )}

      {/* ─── CREATE CUSTOM TAB ─── */}
      {activeTab === "create-custom" && (
        <div className="card p-4 shadow">
          <h5 className="card-title mb-4">➕ Create a New Voucher</h5>

          {customError && (
            <Alert
              variant="danger"
              dismissible
              onClose={() => setCustomError(null)}
            >
              {customError}
            </Alert>
          )}

          {customSuccess && (
            <Alert
              variant="success"
              dismissible
              onClose={() => setCustomSuccess(null)}
            >
              {customSuccess}
            </Alert>
          )}

          <form onSubmit={handleCreateCustomSubmit}>
            <div className="row g-3 mb-3">
              <div className="col-md-6">
                <label className="form-label fw-bold">Voucher Name *</label>
                <input
                  type="text"
                  className="form-control"
                  name="name"
                  value={customForm.name}
                  onChange={handleCustomFormChange}
                  placeholder="e.g., PixelMoon ₹1000 Game Credits"
                  required
                />
              </div>
              <div className="col-md-6">
                <label className="form-label fw-bold">
                  Voucher Code (Manual)
                </label>
                <input
                  type="text"
                  className="form-control"
                  name="code"
                  value={customForm.code}
                  onChange={handleCustomFormChange}
                  placeholder="Leave empty for auto-generated code"
                />
              </div>
            </div>

            <div className="row g-3 mb-3">
              <div className="col-md-6">
                <label className="form-label fw-bold">Type *</label>
                <select
                  className="form-select"
                  name="type"
                  value={customForm.type}
                  onChange={handleCustomFormChange}
                  required
                >
                  <option value="manual">📝 Manual</option>
                  <option value="auto">🤖 Auto</option>
                  <option value="smileone">😊 Smile.one</option>
                  <option value="moo">🐄 MOO</option>
                  <option value="custom">⭐ Custom</option>
                </select>
              </div>
            </div>

            <div className="row g-3 mb-3">
              <div className="col-md-6">
                <label className="form-label fw-bold">Denomination (₹) *</label>
                <input
                  type="number"
                  className="form-control"
                  name="denomination"
                  value={customForm.denomination}
                  onChange={handleCustomFormChange}
                  placeholder="1000"
                  min="1"
                  step="1"
                  required
                />
              </div>
              <div className="col-md-6">
                <label className="form-label fw-bold">Price (₹) *</label>
                <input
                  type="number"
                  className="form-control"
                  name="price"
                  value={customForm.price}
                  onChange={handleCustomFormChange}
                  placeholder="999.99"
                  min="0.01"
                  step="0.01"
                  required
                />
              </div>
            </div>

            <div className="mb-3">
              <label className="form-label fw-bold">Description</label>
              <textarea
                className="form-control"
                name="description"
                value={customForm.description}
                onChange={handleCustomFormChange}
                placeholder="Optional description for this voucher"
                rows="3"
              />
            </div>

            <div className="row g-3 mb-4">
              <div className="col-md-6">
                <label className="form-label fw-bold">Expires At</label>
                <input
                  type="date"
                  className="form-control"
                  name="expiresAt"
                  value={customForm.expiresAt}
                  onChange={handleCustomFormChange}
                />
              </div>
              <div className="col-md-6">
                <label className="form-label fw-bold">Max Uses</label>
                <input
                  type="number"
                  className="form-control"
                  name="maxUses"
                  value={customForm.maxUses}
                  onChange={handleCustomFormChange}
                  min="1"
                  step="1"
                />
              </div>
            </div>

            <div className="d-flex gap-2 justify-content-end">
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() =>
                  setCustomForm({
                    name: "",
                    code: "",
                    type: "manual",
                    denomination: "",
                    price: "",
                    description: "",
                    expiresAt: "",
                    maxUses: 1,
                  })
                }
              >
                Clear
              </button>
              <button
                type="submit"
                className="btn btn-success"
                disabled={creatingCustom}
              >
                {creatingCustom ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    Creating…
                  </>
                ) : (
                  "✅ Create Voucher"
                )}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* ─── CREATE (JSON Rows) TAB ─── */}
      {activeTab === "create-json" && (
        <div className="card p-4 shadow">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h5 className="card-title mb-0">📝 Bulk Create (JSON Rows)</h5>
            <button
              className="btn btn-outline-primary btn-sm"
              onClick={addRow}
              aria-label="Add new row"
            >
              ➕ Add Row
            </button>
          </div>

          {createJSONError && (
            <Alert
              variant="danger"
              dismissible
              onClose={() => setCreateJSONError(null)}
            >
              {createJSONError}
            </Alert>
          )}

          {createJSONResult && (
            <Alert
              variant="success"
              dismissible
              onClose={() => setCreateJSONResult(null)}
            >
              <div className="mb-2">
                <strong>
                  ✅ Processed {createJSONResult.insertedCount} of {rows.length}{" "}
                  vouchers
                </strong>
              </div>
              {createJSONResult.duplicates?.length > 0 && (
                <div className="mb-2">
                  <strong>
                    ⚠️ Duplicates ({createJSONResult.duplicates.length}):
                  </strong>
                  <ul className="ms-3 mb-0">
                    {createJSONResult.duplicates.map((d, i) => (
                      <li key={i} className="small">
                        Row {d.index}: {d.message}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {createJSONResult.invalid?.length > 0 && (
                <div>
                  <strong>
                    ❌ Invalid ({createJSONResult.invalid.length}):
                  </strong>
                  <ul className="ms-3 mb-0">
                    {createJSONResult.invalid.map((i, idx) => (
                      <li key={idx} className="small">
                        Row {i.index}: {i.errors.join(", ")}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </Alert>
          )}

          <div className={styles.tableResponsive}>
            <table className="table table-striped align-middle">
              <thead className="table-light">
                <tr>
                  <th>Code</th>
                  <th>Name</th>
                  <th>Type</th>
                  <th>Denomination</th>
                  <th>Price</th>
                  <th>Description</th>
                  <th>Remove</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, idx) => (
                  <tr key={idx}>
                    <td>
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        value={row.code}
                        onChange={(e) =>
                          handleRowChange(idx, "code", e.target.value)
                        }
                        placeholder="AUTO-GENERATED"
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        value={row.name}
                        onChange={(e) =>
                          handleRowChange(idx, "name", e.target.value)
                        }
                        placeholder="Voucher name"
                        required
                      />
                    </td>
                    <td>
                      <select
                        className="form-select form-select-sm"
                        value={row.type}
                        onChange={(e) =>
                          handleRowChange(idx, "type", e.target.value)
                        }
                      >
                        <option value="smileone">Smile.one</option>
                        <option value="moo">MOO</option>
                        <option value="custom">Custom</option>
                      </select>
                    </td>
                    <td>
                      <input
                        type="number"
                        className="form-control form-control-sm"
                        value={row.denomination}
                        onChange={(e) =>
                          handleRowChange(idx, "denomination", e.target.value)
                        }
                        placeholder="1000"
                        min="1"
                        required
                      />
                    </td>
                    <td>
                      <input
                        type="number"
                        className="form-control form-control-sm"
                        value={row.price}
                        onChange={(e) =>
                          handleRowChange(idx, "price", e.target.value)
                        }
                        placeholder="999.99"
                        step="0.01"
                        min="0"
                        required
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        value={row.description}
                        onChange={(e) =>
                          handleRowChange(idx, "description", e.target.value)
                        }
                        placeholder="Optional"
                      />
                    </td>
                    <td>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => removeRow(idx)}
                        disabled={rows.length === 1}
                        title="Remove row"
                      >
                        🗑️
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="d-flex gap-2 justify-content-end mt-4">
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={() =>
                setRows([
                  {
                    code: "",
                    type: "smileone",
                    denomination: "",
                    price: "",
                    name: "",
                    description: "",
                  },
                ])
              }
            >
              Clear All
            </button>
            <button
              className="btn btn-primary"
              onClick={handleCreateJSONSubmit}
              disabled={creatingJSON}
            >
              {creatingJSON ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  Submitting…
                </>
              ) : (
                "✅ Submit All"
              )}
            </button>
          </div>
        </div>
      )}

      {/* ─── CREATE (CSV Upload) TAB ─── */}
      {activeTab === "create-csv" && (
        <div className="card p-4 shadow">
          <h5 className="card-title mb-4">📤 Upload Vouchers via CSV</h5>

          <div className="alert alert-info mb-4">
            <strong>📋 CSV Format:</strong> code, name, type, denomination,
            price, description, expiresAt, maxUses
          </div>

          {createCSVError && (
            <Alert
              variant="danger"
              dismissible
              onClose={() => setCreateCSVError(null)}
            >
              {createCSVError}
            </Alert>
          )}

          {createCSVResult && (
            <Alert
              variant="success"
              dismissible
              onClose={() => setCreateCSVResult(null)}
            >
              <div className="mb-2">
                <strong>
                  ✅ Successfully imported {createCSVResult.insertedCount}{" "}
                  vouchers!
                </strong>
              </div>
              {createCSVResult.duplicates?.length > 0 && (
                <div className="mb-2">
                  <strong>
                    ⚠️ Duplicates skipped: {createCSVResult.duplicates.length}
                  </strong>
                </div>
              )}
              {createCSVResult.invalid?.length > 0 && (
                <div>
                  <strong>
                    ❌ Invalid rows: {createCSVResult.invalid.length}
                  </strong>
                </div>
              )}
            </Alert>
          )}

          <div className="card bg-light p-4 mb-4">
            <input
              type="file"
              id="voucher-csv-input"
              className="form-control"
              accept=".csv"
              onChange={onCsvFileChange}
              disabled={creatingCSV}
              aria-label="Select CSV file"
            />
            {csvFile && (
              <small className="text-muted d-block mt-2">
                📁 Selected: {csvFile.name} ({(csvFile.size / 1024).toFixed(2)}{" "}
                KB)
              </small>
            )}
          </div>

          {creatingCSV && csvProgress > 0 && (
            <div className="mb-4">
              <small>Uploading: {csvProgress}%</small>
              <div className="progress" style={{ height: "25px" }}>
                <div
                  className="progress-bar progress-bar-striped progress-bar-animated"
                  style={{ width: `${csvProgress}%` }}
                  role="progressbar"
                  aria-valuenow={csvProgress}
                  aria-valuemin={0}
                  aria-valuemax={100}
                />
              </div>
            </div>
          )}

          <button
            className="btn btn-success btn-lg w-100"
            onClick={handleCreateCSVSubmit}
            disabled={creatingCSV || !csvFile}
          >
            {creatingCSV ? (
              <>
                <Spinner size="sm" className="me-2" />
                Uploading…
              </>
            ) : (
              "📤 Upload CSV"
            )}
          </button>
        </div>
      )}

      {/* ─── EDIT MODAL ─── */}
      <Modal
        show={editModal.show}
        onHide={() => setEditModal({ show: false, id: null, data: {} })}
      >
        <Modal.Header closeButton>
          <Modal.Title>✏️ Edit Voucher</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3">
            <label className="form-label fw-bold">Price</label>
            <input
              type="number"
              className="form-control"
              value={editModal.data.price}
              onChange={(e) =>
                setEditModal((prev) => ({
                  ...prev,
                  data: { ...prev.data, price: e.target.value },
                }))
              }
              min="0"
              step="0.01"
            />
          </div>
          <div className="mb-3">
            <label className="form-label fw-bold">Status</label>
            <select
              className="form-select"
              value={editModal.data.status}
              onChange={(e) =>
                setEditModal((prev) => ({
                  ...prev,
                  data: { ...prev.data, status: e.target.value },
                }))
              }
            >
              <option value="active">Active</option>
              <option value="redeemed">Redeemed</option>
              <option value="expired">Expired</option>
            </select>
          </div>
          <div className="mb-3">
            <label className="form-label fw-bold">Expires At</label>
            <input
              type="date"
              className="form-control"
              value={editModal.data.expiresAt}
              onChange={(e) =>
                setEditModal((prev) => ({
                  ...prev,
                  data: { ...prev.data, expiresAt: e.target.value },
                }))
              }
            />
          </div>
          <div className="mb-3">
            <label className="form-label fw-bold">Max Uses</label>
            <input
              type="number"
              className="form-control"
              value={editModal.data.maxUses}
              onChange={(e) =>
                setEditModal((prev) => ({
                  ...prev,
                  data: { ...prev.data, maxUses: e.target.value },
                }))
              }
              min="1"
            />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setEditModal({ show: false, id: null, data: {} })}
          >
            Cancel
          </Button>
          <Button variant="primary" onClick={handleUpdateVoucher}>
            Save Changes
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ─── ARCHIVE EXPIRED MODAL ─── */}
      <Modal
        show={archiveModal.show}
        onHide={() => setArchiveModal({ show: false })}
      >
        <Modal.Header closeButton>
          <Modal.Title>📦 Archive Expired Vouchers</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Are you sure you want to archive all expired vouchers? This action
            cannot be undone.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setArchiveModal({ show: false })}
          >
            Cancel
          </Button>
          <Button variant="warning" onClick={handleArchiveExpired}>
            Yes, Archive All
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default VoucherManagement;
